import {Crop, X} from 'lucide-react'
import {ChangeEvent, ReactNode, useEffect, useState} from 'react'
import {Button} from './button'
import {useImageCropper, type ImageCropConfig} from './image-crop-dialog'
import {SizableText} from './text'
import {cn} from './utils'
import {toast} from './toast'
import {COVER_IMAGE_POLICY, IMAGE_FILE_ACCEPT, processImage} from './image-processing'

/** Props for the ImageForm component. */
export interface ImageFormProps {
  label?: string
  emptyLabel?: string
  suggestedSize?: string
  id?: string
  url?: string
  uploadOnChange?: boolean
  height?: number
  width?: number
  emptyContent?: ReactNode
  /**
   * Enables cropping. Choosing a file opens the cropper rather than accepting
   * the image as-is, and only the cropped result reaches onImageUpload.
   */
  crop?: ImageCropConfig
  /**
   * Optional async function that uploads a File and resolves to its URL.
   * When omitted and `uploadOnChange` is true, the upload step is skipped.
   */
  fileUpload?: (file: File) => Promise<string>
  onImageUpload?: (avatar: string | File) => Awaited<void>
  /** Reports whether crop selection, image preparation, or upload is pending. */
  onPendingChange?: (pending: boolean) => void
  onRemove?: () => void
}

/**
 * Form control for selecting, previewing, and optionally uploading an image.
 * Renders an image preview when `url` is set, and an overlaid file input for
 * picking a replacement. Pass `fileUpload` to have the selected file uploaded
 * automatically when `uploadOnChange` is true; omit it to receive the raw File
 * via `onImageUpload` instead.
 */
export function ImageForm({
  url,
  label,
  id,
  onImageUpload,
  onRemove,
  onPendingChange,
  emptyLabel,
  suggestedSize = '1920px x 1080px',
  uploadOnChange = true,
  height,
  width,
  fileUpload,
  emptyContent,
  crop,
  ...props
}: ImageFormProps) {
  const [processing, setProcessing] = useState(false)
  const deliver = async (file: File, resetInput?: () => void) => {
    if (!onImageUpload) return
    if (uploadOnChange) {
      if (!fileUpload) return
      setProcessing(true)
      return processImage(file, COVER_IMAGE_POLICY)
        .then(fileUpload)
        .then((data) => {
          onImageUpload(data)
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : String(error)
          console.error(`Failed to upload icon: ${message}`, error)
          toast.error(message)
        })
        .finally(() => {
          setProcessing(false)
          resetInput?.()
        })
    } else {
      onImageUpload(file)
    }
  }

  const cropper = useImageCropper({crop, onCropped: deliver})
  const pending = processing || cropper.isOpen
  useEffect(() => {
    onPendingChange?.(pending)
  }, [onPendingChange, pending])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    const fileList = event.target.files
    const file = fileList?.[0]
    if (!file) return
    if (!onImageUpload) return

    event.target.value = ''
    if (!cropper.pick(file)) {
      setProcessing(true)
      processImage(file, COVER_IMAGE_POLICY)
        .then(deliver)
        .catch((error: unknown) => {
          toast.error(error instanceof Error ? error.message : 'Could not process this image')
        })
        .finally(() => setProcessing(false))
    }
  }

  const image = url ? (
    <div className="bg-muted flex-1 overflow-hidden rounded-md">
      <img
        alt="Image preview"
        src={url}
        key={url}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          objectFit: 'cover',
        }}
      />
    </div>
  ) : null

  if (!onImageUpload) return image

  return (
    <div
      className="group group-icon relative flex w-auto items-end self-stretch overflow-hidden rounded-md"
      style={width ? {width, flex: 'none'} : undefined}
    >
      <div
        className="relative w-full self-stretch overflow-hidden"
        style={{
          minHeight: height || 60,
        }}
        {...props}
      >
        <input
          type="file"
          accept={IMAGE_FILE_ACCEPT}
          disabled={pending}
          onChange={handleFileChange}
          style={{
            opacity: 0,
            display: 'flex',
            position: 'absolute',
            left: 0,
            backgroundColor: 'blue',
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 20,
            cursor: 'pointer',
          }}
        />
        {emptyLabel && !url && !emptyContent ? (
          <div className="bg-muted pointer-events-none absolute z-50 h-full items-center justify-center gap-2 opacity-100">
            <SizableText size="xs" className="text-muted-foreground text-center">
              {emptyLabel}
            </SizableText>
          </div>
        ) : null}

        {image || (
          <div className="bg-muted border-border group-hover:border-muted-foreground/50 group-focus-within:border-muted-foreground/50 group-hover:bg-muted/70 pointer-events-none absolute z-50 flex h-full w-full flex-col items-center justify-center gap-0 rounded-md border border-dashed opacity-100 transition-colors">
            {emptyContent ?? (
              <>
                <SizableText size="xs" weight="bold" className="text-muted-foreground text-center">
                  {url ? 'Update Cover' : emptyLabel || 'Add Cover'}
                </SizableText>
                <SizableText size="xs" className="text-muted-foreground text-center">
                  {suggestedSize}
                </SizableText>
              </>
            )}
          </div>
        )}
      </div>
      {cropper.source && url ? (
        <Button
          size="icon"
          aria-label="Adjust crop"
          className={cn('absolute top-0 z-50 opacity-0 group-hover:opacity-100', onRemove ? 'right-8' : 'right-0')}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            cropper.reopen()
          }}
        >
          <Crop className="size-3" />
        </Button>
      ) : null}
      {onRemove && url ? (
        <Button
          size="icon"
          className="grouo-hover:pointer-events-all absolute top-0 right-0 z-50 opacity-0 group-hover:opacity-100"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            cropper.clear()
            onRemove()
          }}
        >
          <X className="size-3" />
        </Button>
      ) : null}
      {cropper.dialog}
    </div>
  )
}
